import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus } from 'lucide-react';
import { ChevronUp, EyeOff, Eye } from 'lucide-react';
import Header from './Header';
import CreateRaidLogDialog from './CreateRaidLogDialog';
import { getUserInfo } from './apiUtils';
import CopyRaidLogDialog from './CopyRaidLogDialog';
import ManageRaidLogCommentsDialog from './ManageRaidLogCommentsDialog';
import ManageLinksDialog from './ManageLinksDialog';
import ManageRaidLogAssignees from './ManageRaidLogAssignees';
import { useSearchParams } from 'react-router-dom';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '700px', // Increased from 500px to 700px
    maxWidth: '90vw', // Added to ensure responsiveness
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxHeight: '90vh', // Added to ensure it fits on smaller screens
    overflowY: 'auto' // Added scrolling for taller dialogs
  }
};




const RaidLogManagement = () => {
  // State for RAID log data
  const [raidLogs, setRaidLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // State for filters
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [criticalityFilter, setCriticalityFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [loggedByFilter, setLoggedByFilter] = useState([]);

  // State for date filters
  const [createdDateFilter, setCreatedDateFilter] = useState({ startDate: '', endDate: '' });
  const [updatedDateFilter, setUpdatedDateFilter] = useState({ startDate: '', endDate: '' });
  const [dueDateFilter, setDueDateFilter] = useState({ startDate: '', endDate: '' });

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: 'created_date', direction: 'asc' });

  // State for available filter options
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableCriticalities, setAvailableCriticalities] = useState([]);
  const [availablePriorities, setAvailablePriorities] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);


const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;


  const [selectedRaidLogs, setselectedRaidLogs] = useState(new Set());
  const [isUpdateStatusDialogOpen, setIsUpdateStatusDialogOpen] = useState(false);
  const [isUpdateCriticalityDialogOpen, setIsUpdateCriticalityDialogOpen] = useState(false);
  const [isUpdatePriorityDialogOpen, setIsUpdatePriorityDialogOpen] = useState(false); // New state for update priority dialog
  const [selectedRequirementForCopy, setSelectedRequirementForCopy] = useState(null);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isManageRaidLogCommentsDialogOpen, setIsManageRaidLogCommentsDialogOpen] = useState(false);
  const [isManageLinksDialogOpen, setIsManageLinksDialogOpen] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [selectedRaidLogForCopy, setSelectedRaidLogForCopy] = useState(null);
  const [isManageAssigneesDialogOpen, setIsManageAssigneesDialogOpen] = useState(false);



const [allAvailableStatuses, setAllAvailableStatuses] = useState([]);
const [allAvailablePriorities, setAllAvailablePriorities] = useState([]);
const [allAvailableCriticalities, setAllAvailableCriticalities] = useState([]);

const [showFilters, setShowFilters] = useState(false);


const [selectedRaidLogType, setSelectedRaidLogType] = useState('');
const [selectedRaidLogIdWithPrefix, setSelectedRaidLogIdWithPrefix] = useState('');

  // Mock credentials (in a real app, these would come from auth context or similar)

  // Dialog state for messages
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const login_token = localStorage.getItem('authToken');
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');
  const [redirectPath, setRedirectPath] = useState('');

const [assigneesFilter, setAssigneesFilter] = useState([]);
const [availableAssignees, setAvailableAssignees] = useState([]);

const [formError, setFormError] = useState('');

const [selectedUserId, setSelectedUserId] = useState('');

// Add edit mode state
const [isEditMode, setIsEditMode] = useState(false);


const [successMessage, setSuccessMessage] = useState('');

  //Input parameters coming into this component from the home page
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');



const generateRaidLogDirectLink = (raidLog) => {
  const baseUrl = window.location.origin;
  const currentPath = '/RaidLogManagement';
  const projectId = currentProject;

  return `${baseUrl}${currentPath}?project=${encodeURIComponent(projectId)}&raidId=${encodeURIComponent(raidLog.raid_id)}&action=edit`;
};


// UPDATED: New permissions state based on IntegrationSystems
const [permissions, setPermissions] = useState({
  canView: true,
  canCreateOrEditRaidLog: false,
  canUpdateStatus: false,
  canUpdateCriticality: false,
  canUpdatePriority: false,
  canCopyRaidLog: false,
  canAddAssignees: false,
  canRemoveAssignees: false,
  // Keep these for existing functionality (no permission restrictions requested)
  canManageComments: true,
  canManageLinks: true,
  canManageAssignees: true
});

const fetchCompleteStatusLists = async () => {
  try {
    // Fetch all statuses
    const statusResponse = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: 'RAID_STATUS',
        search_query: ''
      })
    });

    const statusData = await statusResponse.json();
    if (statusData.status === 'Success' && statusData.entity_statuses) {
      const statuses = statusData.entity_statuses.map(item => item.status);
      setAllAvailableStatuses(statuses);
      console.log('Complete statuses from API:', statuses);
    }

    // Fetch all priorities
    const priorityResponse = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: 'RAID_PRIORITY',
        search_query: ''
      })
    });

    const priorityData = await priorityResponse.json();
    if (priorityData.status === 'Success' && priorityData.entity_statuses) {
      const priorities = priorityData.entity_statuses.map(item => item.status);
      setAllAvailablePriorities(priorities);
      console.log('Complete priorities from API:', priorities);
    }

    // Fetch all criticalities
    const criticalityResponse = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: 'RAID_CRITICALITY',
        search_query: ''
      })
    });

    const criticalityData = await criticalityResponse.json();
    if (criticalityData.status === 'Success' && criticalityData.entity_statuses) {
      const criticalities = criticalityData.entity_statuses.map(item => item.status);
      setAllAvailableCriticalities(criticalities);
      console.log('Complete criticalities from API:', criticalities);
    }

  } catch (error) {
    console.error('Error fetching complete status lists:', error);
  }
};

// Handle opening the dialog for editing
const handleEditRaidLog = (raidLog) => {

  // Check permission before allowing edit
//  if (!permissions.canCreateOrEditRaidLog) {
//    setDialogMessage('You do not have permission to edit RAID logs');
//    setShowDialog(true);
//    return;
//  }

  setIsEditMode(true);

  // Make a copy of the raid log
  const formattedRaidLog = { ...raidLog };

  console.log('raid log:', raidLog);
  console.log('Editing RAID log:', formattedRaidLog);

  // Set the form data
  setNewRaidLog(formattedRaidLog);

  console.log('New RAID log:', newRaidLog);

  // IMPORTANT: Set the selected user ID and don't let it get overridden
  setSelectedUserId(raidLog.raid_logged_by_user || '');

  // Open the dialog
  setShowCreateDialog(true);
};

// Fetch assignees for filter

// Fetch assignees for filter
const fetchAssignees = async () => {
  try {
    const response = await fetch(appConfig.getUrl('/get_raid_log_assignee_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        raid_type: "",
        raid_id: ""
      })
    });

    const data = await response.json();

    if (data.status === 'Success' && data.assignee_list) {
      // Store both user ID and name for the filter options
      const assignees = data.assignee_list.map(item => ({
        id: item.raid_owner_user_id,
        name: item.user_name
      }));
      // Remove duplicates based on user ID
      const uniqueAssignees = assignees.filter((assignee, index, self) =>
        index === self.findIndex(a => a.id === assignee.id)
      );
      setAvailableAssignees(uniqueAssignees);
    } else {
      console.error('Failed to fetch assignees:', data.status_description);
    }
  } catch (error) {
    console.error('Error fetching assignees:', error);
  }
};








const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return '';

    // Format to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};




const handleManageLinksClick = () => {
  // Get the ID of the first selected raid log
  const raidLogId = Array.from(selectedRaidLogs)[0];

  // Find the corresponding raid log to get its type and ID with prefix
  const raidLog = raidLogs.find(log => log.raid_id === raidLogId);

  if (raidLog) {
    // Set the selected raid log type for passing to the dialog
    setSelectedRaidLogType(raidLog.raid_type);
    setSelectedRaidLogIdWithPrefix(raidLog.raid_id_with_prefix);
    setIsManageLinksDialogOpen(true);
  } else {
    setDialogMessage('Selected RAID log not found');
    setShowDialog(true);
  }
};

const handleManageAssigneesClick = () => {
  // Get the ID of the first selected raid log
  const raidLogId = Array.from(selectedRaidLogs)[0];

  // Find the corresponding raid log to get its type and ID with prefix
  const raidLog = raidLogs.find(log => log.raid_id === raidLogId);

  if (raidLog) {
    // Set the selected raid log type for passing to the dialog
    setSelectedRaidLogType(raidLog.raid_type);
    setSelectedRaidLogIdWithPrefix(raidLog.raid_id_with_prefix);
    setIsManageAssigneesDialogOpen(true);
  } else {
    setDialogMessage('Selected RAID log not found');
    setShowDialog(true);
  }
};






const handleCopyButtonClick = () => {


  // Check permission first
  if (!permissions.canCopyRaidLog) {
    setDialogMessage('You do not have permission to copy RAID logs');
    setShowDialog(true);
    return;
  }



  if (selectedRaidLogs.size !== 1) {
    setDialogMessage('Please select exactly one RAID log to copy');
    setShowDialog(true);
    return;
  }

  const raidLogId = Array.from(selectedRaidLogs)[0];
  const raidLog = raidLogs.find(log => log.raid_id === raidLogId);

  if (raidLog) {
    setSelectedRaidLogForCopy(raidLog);
    setIsCopyDialogOpen(true);
  } else {
    setDialogMessage('Selected RAID log not found');
    setShowDialog(true);
  }
};




const handleUpdateStatus = async (newStatus) => {
  try {
    const response = await fetch(appConfig.getUrl('/update_raid_log_status'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        raid_ids: Array.from(selectedRaidLogs),
        status: newStatus
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
      // CHANGED: Use inline success message instead of dialog
      setSuccessMessage('RAID log status updated successfully');
      setIsUpdateStatusDialogOpen(false);
          console.log('Fetch raid log call number 3');
            console.log('Mode:', mode);
            console.log('AssigneesFilter:', assigneesFilter);
            console.log('Corporate Account:', corporateAccount);
            console.log('===============================');
      await fetchRaidLogs();
      setselectedRaidLogs(new Set());

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } else {
      throw new Error(data.status_description || 'Failed to update status');
    }
  } catch (error) {
    // Keep error dialog for errors
    setDialogMessage(error.message);
    setShowDialog(true);
  }
};



const handleUpdatePriority = async (newPriority) => {
  try {
    const response = await fetch(appConfig.getUrl('/update_raid_log_priority'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        raid_ids: Array.from(selectedRaidLogs),
        priority: newPriority
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
      // CHANGED: Use inline success message instead of dialog
      setSuccessMessage('RAID log priority updated successfully');
      setIsUpdatePriorityDialogOpen(false);
              console.log('Fetch raid log call number 4');
            console.log('Mode:', mode);
            console.log('AssigneesFilter:', assigneesFilter);
            console.log('Corporate Account:', corporateAccount);
            console.log('===============================');
      await fetchRaidLogs();
      setselectedRaidLogs(new Set());

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } else {
      throw new Error(data.status_description || 'Failed to update priority');
    }
  } catch (error) {
    // Keep error dialog for errors
    setDialogMessage(error.message);
    setShowDialog(true);
  }
};



const handleUpdateCriticality = async (newCriticality) => {
  try {
    const response = await fetch(appConfig.getUrl('/update_raid_log_criticality'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        raid_ids: Array.from(selectedRaidLogs),
        criticality: newCriticality
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
      // CHANGED: Use inline success message instead of dialog
      setSuccessMessage('RAID log criticality updated successfully');
      setIsUpdateCriticalityDialogOpen(false);
          console.log('Fetch raid log call number 5');
    console.log('Mode:', mode);
    console.log('AssigneesFilter:', assigneesFilter);
    console.log('Corporate Account:', corporateAccount);
    console.log('===============================');
      await fetchRaidLogs();
      setselectedRaidLogs(new Set());

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } else {
      throw new Error(data.status_description || 'Failed to update criticality');
    }
  } catch (error) {
    // Keep error dialog for errors
    setDialogMessage(error.message);
    setShowDialog(true);
  }
};





// Replace the existing UpdateStatusDialog, UpdatePriorityDialog, and UpdateCriticalityDialog components
// in RaidLogManagement.js with these updated versions:

const UpdateStatusDialog = ({ isOpen, onClose, onSubmit, selectedIds, availableStatuses }) => {
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus('');
    }
  }, [isOpen]);




// Add this useEffect after your existing useEffects
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const raidId = urlParams.get('raidId');
  const action = urlParams.get('action');

  console.log('=== URL Parameter Handler ===');
  console.log('raidId from URL:', raidId);
  console.log('action from URL:', action);
  console.log('raidLogs.length:', raidLogs.length);
  console.log('corporateAccount:', corporateAccount);
  console.log('currentProject:', currentProject);

  // Only process if we have all required data and parameters
  if (raidId && action === 'edit' && raidLogs.length > 0 && corporateAccount && currentProject) {
    console.log('Attempting to find RAID log with ID:', raidId);

    // Find the RAID log by ID (try both string and number comparison)
    const raidLog = raidLogs.find(log => {
      console.log('Checking log ID:', log.raid_id, 'against URL ID:', raidId);
      return log.raid_id === raidId || log.raid_id === parseInt(raidId) || log.raid_id.toString() === raidId;
    });

    if (raidLog) {
      console.log('Found RAID log:', raidLog);
      // Small delay to ensure UI is ready
      setTimeout(() => {
        handleEditRaidLog(raidLog);

        // Clean up URL parameters after opening dialog
        const newUrl = window.location.pathname +
          window.location.search
            .replace(/[?&]raidId=[^&]*/, '')
            .replace(/[?&]action=[^&]*/, '')
            .replace(/^\?&/, '?')
            .replace(/^&/, '?');

        const finalUrl = newUrl === window.location.pathname + '?' ? window.location.pathname : newUrl;
        window.history.replaceState({}, document.title, finalUrl);
      }, 500);
    } else {
      console.log('RAID log not found. Available IDs:', raidLogs.map(log => `${log.raid_id} (${typeof log.raid_id})`));
    }
  } else {
    console.log('Conditions not met:', {
      hasRaidId: !!raidId,
      isEditAction: action === 'edit',
      hasRaidLogs: raidLogs.length > 0,
      hasCorporateAccount: !!corporateAccount,
      hasCurrentProject: !!currentProject
    });
  }
  console.log('=============================');
}, [raidLogs, corporateAccount, currentProject]);



  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={DialogStyles.overlay}>
        <div style={{
          ...DialogStyles.dialog,
          width: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          gap: '0'
        }}>
          {/* Header */}
          <div className="p-6 pb-0">
            <h1 className="text-l font-bold text-blue-800 mt-2 mr-20 text-center">
              Update RAID Log Status
            </h1>
            <div className="flex items-center mb-4 mt-3 justify-center bg-blue-100 p-3 rounded">
              <h2 className="text-l font-bold text-blue-700 text-center">
                {selectedIds.length} item(s) selected
              </h2>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto px-6">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                New Status: <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full p-3 border rounded-lg text-base"
                required
              >
                <option value="">Select a status</option>
                {availableStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer with action buttons */}
          <div className="p-6 pt-4 border-t">
            <div className="flex justify-end items-center gap-4">
              <button
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  backgroundColor: selectedStatus ? '#C8F4DE' : '#f3f4f6',
                  color: selectedStatus ? 'black' : '#9ca3af',
                  border: 'none',
                  borderRadius: '4px',
                }}
                onClick={() => {
                  if (selectedStatus) {
                    onSubmit(selectedStatus);
                  }
                }}
                disabled={!selectedStatus}
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UpdatePriorityDialog = ({ isOpen, onClose, onSubmit, selectedIds, availablePriorities }) => {
  const [selectedPriority, setSelectedPriority] = useState('');

console.log('availablePriorities:', availablePriorities);

  useEffect(() => {
    if (isOpen) {
      setSelectedPriority('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={DialogStyles.overlay}>
        <div style={{
          ...DialogStyles.dialog,
          width: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          gap: '0'
        }}>
          {/* Header */}
          <div className="p-6 pb-0">
            <h1 className="text-l font-bold text-blue-800 mt-2 mr-20 text-center">
              Update RAID Log Priority
            </h1>
            <div className="flex items-center mb-4 mt-3 justify-center bg-blue-100 p-3 rounded">
              <h2 className="text-l font-bold text-blue-700 text-center">
                {selectedIds.length} item(s) selected
              </h2>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto px-6">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                New Priority: <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="w-full p-3 border rounded-lg text-base"
                required
              >
                <option value="">Select a priority level</option>
                {availablePriorities.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer with action buttons */}
          <div className="p-6 pt-4 border-t">
            <div className="flex justify-end items-center gap-4">
              <button
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  backgroundColor: selectedPriority ? '#C8F4DE' : '#f3f4f6',
                  color: selectedPriority ? 'black' : '#9ca3af',
                  border: 'none',
                  borderRadius: '4px',
                }}
                onClick={() => {
                  if (selectedPriority) {
                    onSubmit(selectedPriority);
                  }
                }}
                disabled={!selectedPriority}
              >
                Update Priority
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UpdateCriticalityDialog = ({ isOpen, onClose, onSubmit, selectedIds, availableCriticalities }) => {
  const [selectedCriticality, setSelectedCriticality] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedCriticality('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={DialogStyles.overlay}>
        <div style={{
          ...DialogStyles.dialog,
          width: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          gap: '0'
        }}>
          {/* Header */}
          <div className="p-6 pb-0">
            <h1 className="text-l font-bold text-blue-800 mt-2 mr-20 text-center">
              Update RAID Log Criticality
            </h1>
            <div className="flex items-center mb-4 mt-3 justify-center bg-blue-100 p-3 rounded">
              <h2 className="text-l font-bold text-blue-700 text-center">
                {selectedIds.length} item(s) selected
              </h2>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto px-6">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                New Criticality: <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCriticality}
                onChange={(e) => setSelectedCriticality(e.target.value)}
                className="w-full p-3 border rounded-lg text-base"
                required
              >
                <option value="">Select a criticality level</option>
                {availableCriticalities.map(criticality => (
                  <option key={criticality} value={criticality}>{criticality}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer with action buttons */}
          <div className="p-6 pt-4 border-t">
            <div className="flex justify-end items-center gap-4">
              <button
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  backgroundColor: selectedCriticality ? '#C8F4DE' : '#f3f4f6',
                  color: selectedCriticality ? 'black' : '#9ca3af',
                  border: 'none',
                  borderRadius: '4px',
                }}
                onClick={() => {
                  if (selectedCriticality) {
                    onSubmit(selectedCriticality);
                  }
                }}
                disabled={!selectedCriticality}
              >
                Update Criticality
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};








// UPDATED: New permission checking function based on IntegrationSystems
const checkPermission = async (categoryHeader) => {
  try {
    console.log('Calling API with:', {
      corporateAccount,
      userId,
      currentProject,
      categoryHeader
    });

    const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        user_id: userId,
        project_id: currentProject,
        category_header: categoryHeader,
        category_sub_header: []
      }),
    });

    const data = await response.json();
    console.log('API Response:', data);

    // Create new permissions object
    const newPermissions = {
      canView: true,
      canCreateOrEditRaidLog: false,
      canUpdateStatus: false,
      canUpdateCriticality: false,
      canUpdatePriority: false,
      canCopyRaidLog: false,
      canAddAssignees: false,
      canRemoveAssignees: false,
      // Keep these for existing functionality (no permission restrictions requested)
      canManageComments: true,
      canManageLinks: true,
      canManageAssignees: true
    };

    // Map API response to permission fields
    if (data.user_actions_list) {
      data.user_actions_list.forEach(item => {
        console.log('Processing user action:', item.user_action, 'with access:', item.has_access);

        switch(item.user_action) {
          case 'Create or Edit Raid Log':
            newPermissions.canCreateOrEditRaidLog = item.has_access;
            break;
          case 'Update Status':
            newPermissions.canUpdateStatus = item.has_access;
            break;
          case 'Update Priority':
            newPermissions.canUpdatePriority = item.has_access;
            break;
          case 'Update Criticality':
            newPermissions.canUpdateCriticality = item.has_access;
            break;
          case 'Copy Raid Log Entry':
            newPermissions.canCopyRaidLog = item.has_access;
            break;
          case 'Add Assignees':
            newPermissions.canAddAssignees = item.has_access;
            break;
          case 'Remove Assignees':
            newPermissions.canRemoveAssignees = item.has_access;
            break;
        }
      });
    }

    console.log('Setting new permissions:', newPermissions);
    console.log('Setting permissions:', permissions);
    setPermissions(newPermissions);

    return data.access_status && data.status === 'Success';
  } catch (error) {
    console.error(`Error checking permission for ${categoryHeader}:`, error);
    return false;
  }
};


useEffect(() => {
  const handleClickOutside = (event) => {
    // Handle all filter dropdowns
    const dropdowns = [
      'status-filter-dropdown',
      'type-filter-dropdown',
      'criticality-filter-dropdown',
      'priority-filter-dropdown',
      'logged-by-filter-dropdown',
      'assignees-filter-dropdown'
    ];

    dropdowns.forEach(dropdownId => {
      const dropdown = document.getElementById(dropdownId);
      if (dropdown && dropdown.style.display === 'block') {
        const isClickInside = dropdown.contains(event.target);
        const toggleButton = dropdown.previousElementSibling;
        const isClickOnToggle = toggleButton && toggleButton.contains(event.target);

        if (!isClickInside && !isClickOnToggle) {
          dropdown.style.display = 'none';
        }
      }
    });
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []);

useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      // Close all filter dropdowns on escape
      const dropdowns = [
        'status-filter-dropdown',
        'type-filter-dropdown',
        'criticality-filter-dropdown',
        'priority-filter-dropdown',
        'logged-by-filter-dropdown',
        'assignees-filter-dropdown'
      ];

      dropdowns.forEach(dropdownId => {
        const dropdown = document.getElementById(dropdownId);
        if (dropdown && dropdown.style.display === 'block') {
          dropdown.style.display = 'none';
        }
      });
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, []);

useEffect(() => {
  console.log('Permissions updated:', permissions);
}, [permissions]);


useEffect(() => {
  const checkAllPermissions = async () => {
    if (corporateAccount && userId && currentProject) {
      console.log('Checking permissions for corporateAccount:', corporateAccount, 'userId:', userId, 'currentProject:', currentProject);

      // Call checkPermission and wait for it to complete
      await checkPermission('Raid Log Management');

      // The permissions will be set by the checkPermission function
      // No need to manually set permissions here
    }
  };

  checkAllPermissions();
}, [corporateAccount, userId, currentProject]);



// State for create RAID log dialog
const [showCreateDialog, setShowCreateDialog] = useState(false);
const [isCreating, setIsCreating] = useState(false);
const [newRaidLog, setNewRaidLog] = useState({
  raid_type: '',
  raid_description: '',
  criticality: '',
  priority: '',
  resolution: '',
  comments: '',
  status: '',
  due_date: '',
  created_date: '',
  updated_date: ''
});

// State for dropdown options in create dialog
const [raidTypeOptions, setRaidTypeOptions] = useState([]);
const [statusOptions, setStatusOptions] = useState([]);
const [criticalityOptions, setCriticalityOptions] = useState([]);
const [priorityOptions, setPriorityOptions] = useState([]);




 useEffect(() => {
  console.log('Inside useEffect for initial load');

  const setInitialLoad = async () => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const urlProject = urlParams.get('project');
    const stored_project = localStorage.getItem('current_project');

    if (!login_token) {
      showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
      return;
    }

    try {
      const result = await getUserInfo({
        login_token: login_token
      });

      if (result.success) {
        // Determine which project to use: URL > localStorage > default
        let projectToUse = urlProject || stored_project;

        if ((!projectToUse || projectToUse === 'null') && result.userInfo.default_project !== 'null') {
          projectToUse = result.userInfo.default_project;
        }

        if (!projectToUse || projectToUse === 'null') {
          showErrorDialog('Please select a project on the home page', '/RequirementsPortal');
          return;
        }

        // Update localStorage if URL project is different
        if (urlProject && urlProject !== stored_project) {
          localStorage.setItem('current_project', urlProject);
        }

        setCorporateAccount(result.userInfo.corporate_account);
        setCurrentProject(projectToUse);
        setUserId(result.user_id);
        setUserName(result.userInfo.user_name);
        setAccountDescription(result.userInfo.account_description);

        console.log('User info:', result.userInfo);
        console.log('Corporate Account set to:', result.userInfo.corporate_account);
        console.log('Project set to:', projectToUse);

      } else {
        showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
      }
    } catch (error) {
      console.error('Error calling API:', error);
    }
  };

  setInitialLoad();
}, []);



//useEffect(() => {
//  if (showCreateDialog) {
//    fetchAllDropdownOptions();
//  }
//}, [showCreateDialog]);

// Add a separate useEffect to load dropdown options when dialog opens
useEffect(() => {
  if (showCreateDialog) {
    console.log('Create dialog opened, fetching dropdown options');
    fetchAllDropdownOptions();
  }
}, [showCreateDialog]);


//useEffect(() => {
//  if (corporateAccount && currentProject) {
//    fetchAllDropdownOptions();
//  }
//}, [corporateAccount, currentProject]);

// Handle opening the create dialog
// Handle opening the create dialog
// Updated handleOpenCreateDialog function
const handleOpenCreateDialog = () => {

  if (!permissions.canCreateOrEditRaidLog) {
    setDialogMessage('You do not have permission to create RAID logs');
    setShowDialog(true);
    return;
  }

  console.log('=== CREATE RAID LOG START ===');

  // Reset to create mode
  setIsEditMode(false);

  // Reset the form data
  setNewRaidLog({
    raid_type: '',
    raid_description: '',
    criticality: '',
    priority: '',
    resolution: '',
    comments: '',
    status: '',
    due_date: '',
    created_date: '',
    updated_date: ''
  });

  // Make sure to clear the selected user ID
  setSelectedUserId('');

  // Open the dialog
  setShowCreateDialog(true);

  // Fetch dropdown options
  fetchAllDropdownOptions();
};

useEffect(() => {
  if (corporateAccount) {
    console.log('Corporate account changed to:', corporateAccount);
    fetchAllFilterOptions();
    fetchAssignees();
    fetchCompleteStatusLists();
  }
}, [corporateAccount]);





const handleInputChange = (e) => {
  const { name, value } = e.target;
  console.log('handleInputChange called with:', name, value); // ADD THIS LINE

  if (name === 'due_date') {
    if (value) {
      // Create a date in UTC to avoid timezone shifts
      // First parse the YYYY-MM-DD value from the input
      const [year, month, day] = value.split('-').map(num => parseInt(num, 10));

      // Create a UTC date object (use Date.UTC to explicitly create in UTC)
      const utcDate = new Date(Date.UTC(year, month - 1, day));

      // Store the ISO string
      setNewRaidLog(prev => ({
        ...prev,
        [name]: utcDate.toISOString()
      }));
    } else {
      setNewRaidLog(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  } else {
    setNewRaidLog(prev => ({
      ...prev,
      [name]: value
    }));
  }
};

// Fetch dropdown options for create dialog
const fetchDropdownOptions = async (entity) => {
    console.log(`Fetching ${entity} options...`);
  try {
    const response = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: entity,
        search_query: ""
      })
    });

    const data = await response.json();

    console.log(`Fetched ${entity} options:`, data);

    if (data.status === 'Success' && data.entity_statuses) {
      // Extract options and return them
      return data.entity_statuses.map(item => item.status);
    } else {
      console.error(`Failed to fetch ${entity} options:`, data.status_description);
      return [];
    }
  } catch (error) {
    console.error(`Error fetching ${entity} options:`, error);
    return [];
  }
};

// Fetch all dropdown options for create dialog
const fetchAllDropdownOptions = async () => {
    console.log('Fetching all dropdown options for create dialog');
  const raidTypes = await fetchDropdownOptions('RAID_TYPE');
  const statuses = await fetchDropdownOptions('RAID_STATUS');
  const criticalities = await fetchDropdownOptions('RAID_CRITICALITY');
  const priorities = await fetchDropdownOptions('RAID_PRIORITY');

  setRaidTypeOptions(raidTypes);
  setStatusOptions(statuses);
  setCriticalityOptions(criticalities);
  setPriorityOptions(priorities);

  console.log('priorities fetched JK:', priorities);
};

// Updated handleSubmitRaidLog function for RaidLogManagement.js

// Replace the existing handleSubmitRaidLog function with this updated version
// that handles attachment uploads and returns the result to the dialog component
const handleManageCommentsClick = () => {
  // Make sure there's at least one selected raid log
  if (selectedRaidLogs.size === 0) {
    setDialogMessage('Please select at least one RAID log to manage comments');
    setShowDialog(true);
    return;
  }

  // Get the ID of the first selected raid log
  const raidLogId = Array.from(selectedRaidLogs)[0];

  // Find the corresponding raid log to get its type and ID with prefix
  const raidLog = raidLogs.find(log => log.raid_id === raidLogId);

  if (raidLog) {
    // Set the selected raid log type for passing to the dialog
    setSelectedRaidLogType(raidLog.raid_type);
    setSelectedRaidLogIdWithPrefix(raidLog.raid_id_with_prefix);
    setIsManageRaidLogCommentsDialogOpen(true);
  } else {
    setDialogMessage('Selected RAID log not found');
    setShowDialog(true);
  }
};







// In RaidLogManagement.js, replace the handleSubmitRaidLog function with this updated version:

const handleSubmitRaidLog = async () => {
  setFormError('');
  setIsCreating(true);

  try {
    // Determine which API endpoint to use based on edit mode
    const endpoint = isEditMode
      ? appConfig.getUrl('/update_raid_log')
      : appConfig.getUrl('/create_raid_log');

    // Prepare request body
    const requestBody = {
      corporate_account: corporateAccount,
      project_id: currentProject,
      raid_type: newRaidLog.raid_type,
      raid_description: newRaidLog.raid_description,
      raid_logged_by_user: selectedUserId,
      criticality: newRaidLog.criticality,
      priority: newRaidLog.priority,
      resolution: newRaidLog.resolution,
      comments: newRaidLog.comments,
      status: newRaidLog.status,
      due_date: newRaidLog.due_date
    };

    // Handle due_date formatting
    if (requestBody.due_date) {
      const utcDate = new Date(requestBody.due_date);
      const year = utcDate.getUTCFullYear();
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      requestBody.due_date = `${year}-${month}-${day}`;
    }

    // Add raid_id for update operations
    if (isEditMode) {
      requestBody.raid_id = newRaidLog.raid_id;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (data.status === 'Success') {
      // REMOVED: The parent's success dialog display
      // setDialogMessage(successMessage);
      // setShowDialog(true);

      // Reset form
      setNewRaidLog({
        raid_type: '',
        raid_description: '',
        criticality: '',
        priority: '',
        resolution: '',
        comments: '',
        status: '',
        due_date: ''
      });
      setIsEditMode(false);

      // Refresh RAID logs
          console.log('Fetch raid log call number 6');
    console.log('Mode:', mode);
    console.log('AssigneesFilter:', assigneesFilter);
    console.log('Corporate Account:', corporateAccount);
    console.log('===============================');
      fetchRaidLogs();

      // Return success data to the CreateRaidLogDialog for its own success handling
      return {
        success: true,
        raidId: isEditMode ? newRaidLog.raid_id : data.raid_id,
        raidType: newRaidLog.raid_type,
        raidIdWithPrefix: isEditMode ? newRaidLog.raid_id_with_prefix : data.raid_id_with_prefix,
        // Add a flag to prevent any potential parent success dialog
          isEditMode: isEditMode,

        showParentSuccess: false
      };
    } else {
      setFormError(data.status_description || `Failed to ${isEditMode ? 'update' : 'create'} RAID log entry`);
      return { success: false };
    }
  } catch (error) {
    console.error(`Error ${isEditMode ? 'updating' : 'creating'} RAID log entry:`, error);
    setFormError('Error connecting to the server');
    return { success: false };
  } finally {
    setIsCreating(false);
    // DON'T close the dialog here - let the CreateRaidLogDialog handle it
    // setShowCreateDialog(false);
  }
};









const fetchRaidLogs = async () => {

console.log('Fetching RAID logs with filters:')



  setLoading(true);
  try {
    const response = await fetch(appConfig.getUrl('/get_raid_log_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        filter_by_status: statusFilter.length > 0 ? statusFilter : null,
        filter_by_type: typeFilter.length > 0 ? typeFilter : null,
        filter_by_criticality: criticalityFilter.length > 0 ? criticalityFilter : null,
        filter_by_priority: priorityFilter.length > 0 ? priorityFilter : null,
        filter_by_raid_logged_by_user: loggedByFilter.length > 0 ? loggedByFilter : null,
        filter_by_assignees: assigneesFilter.length > 0 ? assigneesFilter : null,
        search_query: searchFilter,
        sort_criteria: `${sortConfig.key} ${sortConfig.direction}`,
        created_date_start: createdDateFilter.startDate,
        created_date_end: createdDateFilter.endDate,
        updated_date_start: updatedDateFilter.startDate,
        updated_date_end: updatedDateFilter.endDate,
        due_date_start: dueDateFilter.startDate,
        due_date_end: dueDateFilter.endDate,
        user_timezone: userTimezone

      })
    });

    const data = await response.json();
    console.log('Fetched RAID logs - CorporateAccount:', corporateAccount);
    console.log('Fetched RAID logs:', data);
    console.log('Mode:', mode);


    if (data.status === 'Success') {
      // Always set raidLogs to the returned data, even if it's an empty array
      setRaidLogs(data.raid_log_list || []);

      // Extract available filter options from the data
      if (data.raid_log_list && data.raid_log_list.length > 0) {
        // Get unique values for each filter category
        const statuses = [...new Set(data.raid_log_list.map(item => item.status))].filter(Boolean);
        const types = [...new Set(data.raid_log_list.map(item => item.raid_type))].filter(Boolean);
        const criticalities = [...new Set(data.raid_log_list.map(item => item.criticality))].filter(Boolean);
        const priorities = [...new Set(data.raid_log_list.map(item => item.priority))].filter(Boolean);
        const users = [...new Set(data.raid_log_list.map(item => item.raid_logged_by_user))].filter(Boolean);

        // Update filter options only if we have new values
        // This avoids losing options when filtering narrows the results
        if (statuses.length > 0) {
          setAvailableStatuses(prev => {
            const combined = [...new Set([...prev, ...statuses])];
            return combined;
          });
        }

        if (types.length > 0) {
          setAvailableTypes(prev => {
            const combined = [...new Set([...prev, ...types])];
            return combined;
          });
        }

        if (criticalities.length > 0) {
          setAvailableCriticalities(prev => {
            const combined = [...new Set([...prev, ...criticalities])];
            return combined;
          });
        }

        if (priorities.length > 0) {
          setAvailablePriorities(prev => {
            const combined = [...new Set([...prev, ...priorities])];
            return combined;
          });
        }

        if (users.length > 0) {
          setAvailableUsers(prev => {
            const combined = [...new Set([...prev, ...users])];
            return combined;
          });
        }

        // REMOVE THESE LINES - This is what's causing the auto-population:
        // if (statusFilter.length === 0) setStatusFilter(statuses);
        // if (typeFilter.length === 0) setTypeFilter(types);
      }
    } else {
      // Clear the raid logs list when there's an error
      setRaidLogs([]);
    }
  } catch (error) {
    // Clear the raid logs list when there's an error
    setRaidLogs([]);
    console.error('Error fetching RAID logs:', error);
    setError('Error connecting to the server');
    setShowDialog(true);
    setDialogMessage('Error connecting to the server');
  } finally {
    setLoading(false);
  }
};




  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };

  // Initial fetch to get all possible filter options
  const fetchAllFilterOptions = async () => {
    try {
      // This API call with no filters will get all possible options
      const response = await fetch(appConfig.getUrl('/get_raid_log_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          filter_by_status: null,
          filter_by_type: null,
          filter_by_criticality: null,
          filter_by_priority: null,
          filter_by_raid_logged_by_user: null,
          search_query: "",
          sort_criteria: "created_date desc"
        })
      });

      const data = await response.json();

      if (data.status === 'Success' && data.raid_log_list && data.raid_log_list.length > 0) {
        // Extract all possible filter options
        const statuses = [...new Set(data.raid_log_list.map(item => item.status))].filter(Boolean);
        const types = [...new Set(data.raid_log_list.map(item => item.raid_type))].filter(Boolean);
        const criticalities = [...new Set(data.raid_log_list.map(item => item.criticality))].filter(Boolean);
        const priorities = [...new Set(data.raid_log_list.map(item => item.priority))].filter(Boolean);
        const users = [...new Set(data.raid_log_list.map(item => item.raid_logged_by_user))].filter(Boolean);

        setAvailableStatuses(statuses);
        setAvailableTypes(types);
        setAvailableCriticalities(criticalities);
        setAvailablePriorities(priorities);
        setAvailableUsers(users);
      }
    } catch (error) {
      console.error('Error fetching all filter options:', error);
    }
  };

 useEffect(() => {
  // Wait for all required data to be available
  if (!corporateAccount || !currentProject) {
    return;
  }

  // Handle mode-specific filter setup
  if (mode === 'myassignments' && userId && assigneesFilter.length === 0) {
    console.log('Setting assignees filter for myassignments mode');
    setAssigneesFilter([userId]);
    return; // Don't fetch yet, let the next render cycle handle the fetch
  }

  // UPDATED: Handle all different modes properly
  // Only fetch if we have proper filters set for the mode OR if it's a mode that doesn't require special filters
  const shouldWaitForAssigneeFilter = mode === 'myassignments' && assigneesFilter.length === 0;

  if (shouldWaitForAssigneeFilter) {
    console.log('Waiting for assignees filter to be set for myassignments mode');
    return; // Don't fetch until assignees filter is properly set
  }

  console.log('=== SINGLE FETCH TRIGGERED ===');
  console.log('Mode:', mode);
  console.log('AssigneesFilter:', assigneesFilter);
  console.log('Corporate Account:', corporateAccount);
  console.log('===============================');
  fetchRaidLogs();
}, [
  // Core dependencies
  corporateAccount,
  currentProject,
  userId,
  mode,
  // Filter dependencies
  statusFilter,
  typeFilter,
  criticalityFilter,
  priorityFilter,
  loggedByFilter,
  assigneesFilter,
  searchFilter,
  sortConfig,
  createdDateFilter,
  updatedDateFilter,
  dueDateFilter
]);

useEffect(() => {
  console.log('=== URL DEBUG INFO ===');
  console.log('Current URL:', window.location.href);
  console.log('Search params:', window.location.search);
  console.log('Parsed mode from URL:', mode);
  console.log('Available modes should be: myassignments, allassignees, or null');
  console.log('======================');
}, [mode]);



useEffect(() => {
  console.log('=== COMPONENT STATE DEBUG ===');
  console.log('loading:', loading);
  console.log('raidLogs.length:', raidLogs.length);
  console.log('corporateAccount:', corporateAccount);
  console.log('currentProject:', currentProject);
  console.log('userId:', userId);
  console.log('mode:', mode);
  console.log('Component should render:', !!corporateAccount && !!currentProject);
  console.log('==============================');
}, [loading, raidLogs, corporateAccount, currentProject, userId, mode]);




  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    statusFilter,
    typeFilter,
    criticalityFilter,
    priorityFilter,
    loggedByFilter,
    assigneesFilter,
    searchFilter
  ]);

  // Calculate pagination
  const totalPages = Math.ceil(raidLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLogs = raidLogs.slice(startIndex, startIndex + itemsPerPage);

  // Handle search
 const handleSearch = () => {
  setSearchFilter(searchInput);
};

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };





















  // Enhanced Date range picker component
 const DateRangePicker = ({ label, value, onChange, icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(value);
  const dropdownRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalDateRange(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);









// Replace the handleQuickSelect function in your DateRangePicker component with this version:

const handleQuickSelect = (option) => {
  const today = new Date();
  let startDate = '';
  let endDate = '';

  // Helper function to format date in local timezone as YYYY-MM-DD
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  switch(option) {
    case 'today':
      startDate = formatLocalDate(today);
      endDate = startDate;
      break;
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = formatLocalDate(yesterday);
      endDate = startDate;
      break;
    }
    case 'thisWeek': {
      const thisMonday = new Date(today);
      const day = thisMonday.getDay();
      const diff = thisMonday.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
      thisMonday.setDate(diff);
      startDate = formatLocalDate(thisMonday);
      endDate = formatLocalDate(today);
      break;
    }
    case 'lastWeek': {
      const lastMonday = new Date(today);
      const day = lastMonday.getDay();
      lastMonday.setDate(lastMonday.getDate() - day - 6); // Last Monday
      startDate = formatLocalDate(lastMonday);

      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastSunday.getDate() + 6);
      endDate = formatLocalDate(lastSunday);
      break;
    }
    case 'thisMonth': {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate = formatLocalDate(firstOfMonth);
      endDate = formatLocalDate(today);
      break;
    }
    case 'lastMonth': {
      const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      startDate = formatLocalDate(firstOfLastMonth);

      const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      endDate = formatLocalDate(lastOfLastMonth);
      break;
    }
    default:
      break;
  }

  setLocalDateRange({ startDate, endDate });
};


  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [year, month, day] = dateStr.split('-');
      const dateObj = new Date(`${year}-${month}-${day}T12:00:00`);
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return dateObj.toLocaleDateString('en-US', options);
    } catch (e) {
      return dateStr;
    }
  };

  const displayValue = value.startDate || value.endDate
    ? `${value.startDate ? formatDate(value.startDate) : 'Any'} - ${value.endDate ? formatDate(value.endDate) : 'Any'}`
    : 'Any date';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center p-3 h-12 rounded border bg-green-50 text-green-700" // Increased height with h-12 and p-3
      >
        <Calendar size={16} className="text-green-700" />
        <span className="ml-2 text-sm">{label}: {displayValue}</span> {/* Increased text size */}
        <ChevronDown size={16} className="ml-2 text-green-700" />
      </button>

      {isOpen && (
        <div className="absolute mt-1 bg-white border rounded shadow-lg z-10 w-72 p-3">
          {/* Quick select options */}
          <div className="mb-3 border-b pb-2">
            <label className="block text-sm font-medium mb-2">Quick Select:</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleQuickSelect('today')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700" // Increased padding to p-2
              >
                Today
              </button>
              <button
                onClick={() => handleQuickSelect('yesterday')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700" // Increased padding to p-2
              >
                Yesterday
              </button>
              <button
                onClick={() => handleQuickSelect('thisWeek')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700" // Increased padding to p-2
              >
                This Week
              </button>
              <button
                onClick={() => handleQuickSelect('lastWeek')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700" // Increased padding to p-2
              >
                Last Week
              </button>
              <button
                onClick={() => handleQuickSelect('thisMonth')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700" // Increased padding to p-2
              >
                This Month
              </button>
              <button
                onClick={() => handleQuickSelect('lastMonth')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700" // Increased padding to p-2
              >
                Last Month
              </button>
            </div>
          </div>

          {/* Custom date range */}
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">Start Date:</label>
            <input
              type="date"
              value={localDateRange.startDate}
              onChange={(e) => setLocalDateRange({ ...localDateRange, startDate: e.target.value })}
              className="w-full p-2 border rounded h-10" // Increased height with h-10
            />
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">End Date:</label>
            <input
              type="date"
              value={localDateRange.endDate}
              onChange={(e) => setLocalDateRange({ ...localDateRange, endDate: e.target.value })}
              className="w-full p-2 border rounded h-10" // Increased height with h-10
            />
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={() => setLocalDateRange({ startDate: '', endDate: '' })}
              className="text-xs text-red-600 hover:underline p-2" // Added padding
            >
              Clear
            </button>
            <button
              onClick={() => {
                onChange(localDateRange);
                setIsOpen(false);
              }}
              className="px-4 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600" // Increased button size
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

 // Simple dialog component
const Dialog = ({ message, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-medium mb-4">{message}</h3>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

  // Status badge component
  const StatusBadge = ({ status }) => {
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-800";

    if (status === "BLOCKED") {
      bgColor = "bg-red-100";
      textColor = "text-red-800";
    } else if (status === "RESOLVED") {
      bgColor = "bg-green-100";
      textColor = "text-green-800";
    } else if (status === "IN PROGRESS") {
      bgColor = "bg-blue-100";
      textColor = "text-blue-800";
    } else if (status === "PENDING") {
      bgColor = "bg-yellow-100";
      textColor = "text-yellow-800";
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Type badge component
  const TypeBadge = ({ type }) => {
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-800";

    if (type === "RISK") {
      bgColor = "bg-orange-100";
      textColor = "text-orange-800";
    } else if (type === "ACTION") {
      bgColor = "bg-blue-100";
      textColor = "text-blue-800";
    } else if (type === "ISSUE") {
      bgColor = "bg-red-100";
      textColor = "text-red-800";
    } else if (type === "DECISION") {
      bgColor = "bg-purple-100";
      textColor = "text-purple-800";
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs ${bgColor} ${textColor}`}>
        {type}
      </span>
    );
  };

  // Priority badge component
  const PriorityBadge = ({ priority }) => {
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-800";

    if (priority === "High") {
      bgColor = "bg-red-100";
      textColor = "text-red-800";
    } else if (priority === "Medium") {
      bgColor = "bg-yellow-100";
      textColor = "text-yellow-800";
    } else if (priority === "Low") {
      bgColor = "bg-green-100";
      textColor = "text-green-800";
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs ${bgColor} ${textColor}`}>
        {priority}
      </span>
    );
  };





return (
  <div className="min-h-screen bg-gray-50 p-6">
    <Header />

    <div className="mt-12">
      {/* Title bar */}

  {/* Title bar */}

   <h1 className="text-l font-bold text-blue-800 mr-20 text-center">
  {mode === 'myassignments' ? 'My RAID Log Assignments' : 'Manage RAID Log Entries'}
</h1>


  <div className="flex items-center mb-4 justify-between bg-blue-100 p-3 rounded">

    {/* Search bar */}
    <div className="flex items-center">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            // If the input is cleared, immediately clear the search filter
            if (e.target.value === '') {
              setSearchFilter('');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          placeholder="Search RAID logs..."
          className="p-2 pr-10 border rounded w-full"
        />
      <button
        onClick={handleSearch}
        className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
      >
        <Search size={18} />
      </button>
    </div>

  <h2 className="text-l font-bold text-blue-700 text-center flex-1">Project: {currentProject}</h2>



<div className="group relative">
  <button
    disabled={!permissions.canCreateOrEditRaidLog}
    onClick={handleOpenCreateDialog}
    className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!permissions.canCreateOrEditRaidLog ? 'opacity-50' : ''}`}
  >
    <Plus size={18} className="mr-2" />
    Create New
  </button>

  {/* Hover tooltip for disabled state */}
  {!permissions.canCreateOrEditRaidLog && (
    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
      Insufficient permission
      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
    </div>
  )}
</div>





  </div>


{/* Filters section */}
<div className={`mb-4 bg-white rounded-lg shadow transition-all duration-300 ${showFilters ? 'p-4' : 'p-2'}`}>









  <div className="flex items-center justify-between mb-2">
    {/* Toggle button for Filters */}
    <button
      onClick={() => setShowFilters(!showFilters)}
      className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
    >
      {showFilters ? <EyeOff size={16} /> : <Eye size={16} />}
      <span className="ml-2 font-medium">
        {showFilters ? 'Hide' : 'Show'} Filters
      </span>
      {showFilters ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
    </button>

    {/* When collapsed, show active filter count */}
    {!showFilters && (
      <div className="text-sm text-gray-600">
        {[
          statusFilter.length > 0 && `${statusFilter.length} status`,
          typeFilter.length > 0 && `${typeFilter.length} type`,
          criticalityFilter.length > 0 && `${criticalityFilter.length} criticality`,
          priorityFilter.length > 0 && `${priorityFilter.length} priority`,
          loggedByFilter.length > 0 && `${loggedByFilter.length} logged by`,
          assigneesFilter.length > 0 && `${assigneesFilter.length} assignees`,
          (createdDateFilter.startDate || createdDateFilter.endDate) && 'creation date',
          (updatedDateFilter.startDate || updatedDateFilter.endDate) && 'update date',
          (dueDateFilter.startDate || dueDateFilter.endDate) && 'due date'
        ].filter(Boolean).join(', ') || 'No active filters'}
      </div>
    )}
  </div>








  {/* Collapsible filters content */}
  {showFilters && (
    <>



  <div className="flex flex-wrap gap-3 mb-3">
    <div className="flex items-center">
      <Filter size={24} className="text-gray-600 mr-2" />
      <span className="text-sm font-medium text-gray-700">Filters:</span>
    </div>

    {/* Status Filter Dropdown */}
    <div className="relative" style={{ minWidth: '160px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-yellow-50 text-yellow-700"
        onClick={() => {
          const dropdown = document.getElementById('status-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-status-7410179-6e5333.svg" alt="Status Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Status ({statusFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="status-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setStatusFilter([...availableStatuses]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setStatusFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {availableStatuses.map(status => (
          <div
            key={status}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setStatusFilter(prev => {
                if (prev.includes(status)) {
                  return prev.filter(s => s !== status);
                } else {
                  return [...prev, status];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={statusFilter.includes(status)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{status}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Type Filter Dropdown */}
    <div className="relative" style={{ minWidth: '140px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-blue-50 text-blue-700"
        onClick={() => {
          const dropdown = document.getElementById('type-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-category-7854151-6e5333.svg" alt="Type Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Type ({typeFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="type-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setTypeFilter([...availableTypes]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setTypeFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {availableTypes.map(type => (
          <div
            key={type}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setTypeFilter(prev => {
                if (prev.includes(type)) {
                  return prev.filter(t => t !== type);
                } else {
                  return [...prev, type];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={typeFilter.includes(type)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{type}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Criticality Filter Dropdown */}
    <div className="relative" style={{ minWidth: '180px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-pink-50 text-pink-700"
        onClick={() => {
          const dropdown = document.getElementById('criticality-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-critical-status-7295245-6e5333.svg" alt="Criticality Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Criticality ({criticalityFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="criticality-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setCriticalityFilter([...availableCriticalities]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setCriticalityFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {availableCriticalities.map(criticality => (
          <div
            key={criticality}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setCriticalityFilter(prev => {
                if (prev.includes(criticality)) {
                  return prev.filter(c => c !== criticality);
                } else {
                  return [...prev, criticality];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={criticalityFilter.includes(criticality)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{criticality}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Priority Filter Dropdown */}
    <div className="relative" style={{ minWidth: '160px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-purple-50 text-purple-700"
        onClick={() => {
          const dropdown = document.getElementById('priority-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-priority-7620160-6e5333.svg" alt="Priority Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Priority ({priorityFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="priority-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setPriorityFilter([...availablePriorities]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setPriorityFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {availablePriorities.map(priority => (
          <div
            key={priority}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setPriorityFilter(prev => {
                if (prev.includes(priority)) {
                  return prev.filter(p => p !== priority);
                } else {
                  return [...prev, priority];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={priorityFilter.includes(priority)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{priority}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Logged By Filter Dropdown */}
    <div className="relative" style={{ minWidth: '180px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-green-50 text-green-700"
        onClick={() => {
          const dropdown = document.getElementById('logged-by-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-person-7574717-6e5333.svg" alt="Person Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Logged By ({loggedByFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="logged-by-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setLoggedByFilter([...availableUsers]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setLoggedByFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {availableUsers.map(user => (
          <div
            key={user}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setLoggedByFilter(prev => {
                if (prev.includes(user)) {
                  return prev.filter(u => u !== user);
                } else {
                  return [...prev, user];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={loggedByFilter.includes(user)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{user}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Assignees Filter Dropdown */}
    <div className="relative" style={{ minWidth: '180px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-indigo-50 text-indigo-700"
        onClick={() => {
          const dropdown = document.getElementById('assignees-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-team-6990022-6e5333.svg" alt="Team Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Assignees ({assigneesFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="assignees-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setAssigneesFilter(availableAssignees.map(assignee => assignee.id));
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setAssigneesFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {availableAssignees.map(assignee => (
          <div
            key={assignee.id}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setAssigneesFilter(prev => {



        if (prev.includes(assignee.id)) {
          // Allow deselection even if it's the current user
          const newFilter = prev.filter(id => id !== assignee.id);
          console.log('Deselecting assignee:', assignee.id, 'New filter:', newFilter);
          return newFilter;
        } else {
          const newFilter = [...prev, assignee.id];
          console.log('Selecting assignee:', assignee.id, 'New filter:', newFilter);
          return newFilter;
        }



              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={assigneesFilter.includes(assignee.id)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{assignee.name}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Date filters */}
    <div className="float-right flex items-center ml-4 gap-5 h-15 ">
      {/* Created Date filter */}
      <DateRangePicker
        label="Created Date"
        value={createdDateFilter}
        onChange={setCreatedDateFilter}
        icon={<Calendar size={16} />}
      />

      {/* Updated Date filter */}
      <DateRangePicker
        label="Updated Date"
        value={updatedDateFilter}
        onChange={setUpdatedDateFilter}
        icon={<Calendar size={16} />}
      />

      {/* Due Date filter */}
      <DateRangePicker
        label="Due Date"
        value={dueDateFilter}
        onChange={setDueDateFilter}
        icon={<Calendar size={16} />}
      />

      {/* Reset filters button */}
      <button
        onClick={() => {
          setStatusFilter([]);
          setTypeFilter([]);
          setCriticalityFilter([]);
          setPriorityFilter([]);
          setLoggedByFilter([]);
          setAssigneesFilter([]);
          setCreatedDateFilter({ startDate: '', endDate: '' });
          setUpdatedDateFilter({ startDate: '', endDate: '' });
          setDueDateFilter({ startDate: '', endDate: '' });
          setSearchFilter('');
          setSearchInput('');
        }}
        className="ml-auto px-3 py-1 text-lg bg-red-300 text-white rounded hover:bg-red-500 flex items-center"
      >
        <X size={18} className="mr-1" />
        Reset All Filters
      </button>
    </div>
  </div>

    </>
  )}














</div>

      {/* Main content and sidebar in a flex container */}
      <div className="flex gap-6">
        {/* Main content area */}
        <div className="flex-1">
          {/* Main Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : raidLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No RAID log entries found matching your filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-800">
                  <thead className="bg-gray-200">
                    <tr>




                        <th scope="col" className="px-4 py-3 text-center w-10">
                          <input
                            type="checkbox"
                            checked={paginatedLogs.length > 0 && paginatedLogs.every(log => selectedRaidLogs.has(log.raid_id))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all visible logs
                                const newSelected = new Set(selectedRaidLogs);
                                paginatedLogs.forEach(log => newSelected.add(log.raid_id));
                                setselectedRaidLogs(newSelected);
                              } else {
                                // Deselect all visible logs
                                const newSelected = new Set(selectedRaidLogs);
                                paginatedLogs.forEach(log => newSelected.delete(log.raid_id));
                                setselectedRaidLogs(newSelected);
                              }
                            }}
                            className="w-4 h-4"
                          />
                        </th>










                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('raid_id')}
                      >
                        <div className="flex items-center">
                          <span className="underline">ID</span>
                          {sortConfig.key === 'raid_id' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('raid_type')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Type</span>
                          {sortConfig.key === 'raid_type' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('raid_description')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Description</span>
                          {sortConfig.key === 'raid_description' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Status</span>
                          {sortConfig.key === 'status' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('criticality')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Criticality</span>
                          {sortConfig.key === 'criticality' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('priority')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Priority</span>
                          {sortConfig.key === 'priority' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('due_date')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Due Date</span>
                          {sortConfig.key === 'due_date' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('logged_by_user_name')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Logged By</span>
                          {sortConfig.key === 'logged_by_user_name' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('number_of_assignees')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Assignees</span>
                          {sortConfig.key === 'number_of_assignees' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('created_date')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Created</span>
                          {sortConfig.key === 'created_date' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('updated_date')}
                      >
                        <div className="flex items-center">
                          <span className="underline">Last Updated</span>
                          {sortConfig.key === 'updated_date' && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedLogs.map((log) => (
                      <tr
                      key={log.raid_id}
                        className={`hover:bg-blue-50 ${selectedRaidLogs.has(log.raid_id) ? 'bg-blue-50' : ''}`}
                      >




                            <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedRaidLogs.has(log.raid_id)}
                                  onChange={() => {
                                    const newSelected = new Set(selectedRaidLogs);
                                    if (newSelected.has(log.raid_id)) {
                                      newSelected.delete(log.raid_id);
                                    } else {
                                      newSelected.add(log.raid_id);
                                    }
                                    setselectedRaidLogs(newSelected);
                                  }}
                                  className="w-4 h-4"
                                />
                              </td>









                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="group relative">
                            <button
                              className={`text-blue-600 hover:text-blue-800 hover:underline focus:outline-none `}
                              onClick={() => handleEditRaidLog(log)}
                              title={"Click to edit this RAID log" }
                            >
                              {log.raid_id_with_prefix}
                            </button>
                          </div>
                        </td>



                        <td className="px-4 py-3 whitespace-nowrap">
                          <TypeBadge type={log.raid_type} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 max-w-md truncate">{log.raid_description}</div>
                          {log.comments && (
                            <div className="text-xs text-gray-500 mt-1 max-w-md truncate">
                              <span className="font-semibold">Comments:</span> {log.comments}
                            </div>
                          )}
                          {log.resolution && (
                            <div className="text-xs text-gray-500 mt-1 max-w-md truncate">
                              <span className="font-semibold">Resolution:</span> {log.resolution}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={log.status} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs">{log.criticality}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <PriorityBadge priority={log.priority} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs">
                            {log.due_date ? (() => {
                              // Create date object from the string
                              const date = new Date(log.due_date);
                              // Use UTC methods to get year, month, and day
                              const year = date.getUTCFullYear();
                              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                              const day = String(date.getUTCDate()).padStart(2, '0');
                              // Format as MM/DD/YYYY
                              return `${month}/${day}/${year}`;
                            })() : 'Not set'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs">{log.logged_by_user_name}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="px-2 py-1 text-xs">
                            {log.number_of_assignees}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs">
                            {new Date(log.created_date).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs">
                            {new Date(log.updated_date).toLocaleDateString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>


{/* Success Message - ADD THIS */}
{successMessage && (
  <div className="mb-4 p-3 rounded bg-green-50 text-green-700 border border-green-200">
    {successMessage}
  </div>
)}


          {/* Pagination controls */}
          {!loading && raidLogs.length > 0 && (
            <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Total Entries: {raidLogs.length}</span>
                <span>| Showing {raidLogs.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, raidLogs.length)} of {raidLogs.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  className="border rounded p-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar with buttons and icons */}
        <div className="w-64 bg-white border rounded shadow-sm self-start sticky top-6">
          <div className="p-4">
            <h3 className="font-bold text-sm text-orange-700 mb-4 bg-orange-200 p-2 rounded">RAID ACTIONS</h3>

            {/* Status and Criticality buttons */}
            <div className="space-y-2 mb-6">
              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center ${
                  !permissions.canUpdateStatus || selectedRaidLogs.size === 0
                    ? 'text-gray-400'
                    : 'text-blue-700 hover:text-blue-900'
                }`}
                disabled={selectedRaidLogs.size === 0 || !permissions.canUpdateStatus}
                onClick={() => setIsUpdateStatusDialogOpen(true)}
              >
                <img
                  src="/icons/noun-status-7577449-FF824A.svg"
                  alt="Update Status"
                  className={`w-8 h-8 mr-3 ${!permissions.canUpdateStatus || selectedRaidLogs.size === 0 ? 'opacity-50' : ''}`}
                />
                <span className={`flex items-center ${!permissions.canUpdateStatus || selectedRaidLogs.size === 0 ? 'text-orange-400' : 'text-orange-700'}`}>
                  Update Status
                  <img
                    src="/icons/noun-one-to-many-1406451-FF824A.svg"
                    alt="Info"
                    className={`w-8 h-8 ml-2 ${(!permissions.canUpdateStatus || selectedRaidLogs.size == 0 ) ? 'opacity-50' : ''}`}
                  />
                </span>
              </button>

              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center ${
                  !permissions.canUpdateCriticality || selectedRaidLogs.size === 0
                    ? 'text-gray-400'
                    : 'text-blue-700 hover:text-blue-900'
                }`}
                disabled={selectedRaidLogs.size === 0 || !permissions.canUpdateCriticality}
                onClick={() => setIsUpdateCriticalityDialogOpen(true)}
              >
                <img
                  src="/icons/noun-critical-status-7295245-FF824A.svg"
                  alt="Update Criticality"
                  className={`w-8 h-8 mr-3 ${!permissions.canUpdateCriticality || selectedRaidLogs.size === 0 ? 'opacity-50' : ''}`}
                />
                <span className={`flex items-center ${!permissions.canUpdateCriticality || selectedRaidLogs.size === 0 ? 'text-orange-400' : 'text-orange-700'}`}>
                  Update Criticality
                  <img
                    src="/icons/noun-one-to-many-1406451-FF824A.svg"
                    alt="Info"
                    className={`w-8 h-8 ml-2 ${(!permissions.canUpdateCriticality || selectedRaidLogs.size === 0 ) ? 'opacity-50' : ''}`}


                  />
                </span>
              </button>

              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center ${
                  !permissions.canUpdatePriority || selectedRaidLogs.size === 0
                    ? 'text-gray-400'
                    : 'text-blue-700 hover:text-blue-900'
                }`}
                disabled={selectedRaidLogs.size === 0 || !permissions.canUpdatePriority}
                onClick={() => setIsUpdatePriorityDialogOpen(true)}
              >
                <img
                  src="/icons/noun-priority-7620160-FF824A.svg"
                  alt="Update Priority"
                  className={`w-8 h-8 mr-3 ${!permissions.canUpdatePriority || selectedRaidLogs.size === 0 ? 'opacity-50' : ''}`}
                />
                <span className={`flex items-center ${!permissions.canUpdatePriority || selectedRaidLogs.size === 0 ? 'text-orange-400' : 'text-orange-700'}`}>
                  Update Priority
                  <img
                    src="/icons/noun-one-to-many-1406451-FF824A.svg"
                    alt="Info"
                    className={`w-8 h-8 ml-2 ${(!permissions.canUpdatePriority || selectedRaidLogs.size === 0 ) ? 'opacity-50' : ''}`}

                  />
                </span>
              </button>

              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center ${
                  selectedRaidLogs.size !== 1 || !permissions.canCopyRaidLog
                    ? 'text-gray-400'
                    : 'text-blue-700 hover:text-blue-900'
                }`}
                disabled={selectedRaidLogs.size !== 1 || !permissions.canCopyRaidLog}
                onClick={handleCopyButtonClick}
              >
                <img
                  src="/icons/noun-copy-7721290-FF824A.svg"
                  alt="Copy Raid log"
                  className={`w-8 h-8 mr-3 ${selectedRaidLogs.size !== 1  || !permissions.canCopyRaidLog? 'opacity-50' : ''}`}
                />
                <span className={selectedRaidLogs.size !== 1  || !permissions.canCopyRaidLog ? 'text-orange-400' : 'text-orange-700'}>
                  Copy Raid Log
                </span>
              </button>

              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center ${
                  selectedRaidLogs.size  != 1
                    ? 'text-gray-400'
                    : 'text-blue-700 hover:text-blue-900'
                }`}
                disabled={selectedRaidLogs.size  != 1   }
                onClick={handleManageCommentsClick}
              >
                <img
                  src="/icons/noun-comments-6695629-FF824A.svg"
                  alt="Manage Comments"
                  className={`w-8 h-8 mr-3 ${selectedRaidLogs.size != 1   ? 'opacity-50' : ''}`}
                />
                <span className={selectedRaidLogs.size  != 1   ? 'text-orange-400' : 'text-orange-700'}>
                  Manage Comments
                </span>
              </button>

              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center ${
                  selectedRaidLogs.size !== 1 || !permissions.canManageLinks
                    ? 'text-gray-400'
                    : 'text-blue-700 hover:text-orange-900'
                }`}
                disabled={selectedRaidLogs.size !== 1  || !permissions.canManageLinks}
                onClick={handleManageLinksClick}
              >
                <img
                  src="/icons/noun-links-5685687-FF824A.svg"
                  alt="Manage Links"
                  className={`w-8 h-8 mr-3 ${selectedRaidLogs.size !== 1  || !permissions.canManageLinks ? 'opacity-50' : ''}`}
                />
                <span className={selectedRaidLogs.size !== 1  || !permissions.canManageLinks? 'text-orange-400' : 'text-orange-700'}>
                  Manage Links
                </span>
              </button>
            </div>

            {/* Assignee management */}
            <h4 className="font-bold text-xs text-purple-600 bg-purple-200 p-2 mb-2 rounded">RAID ASSIGNMENT</h4>
            <div className="space-y-2 mb-6">
              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center text-purple-700 hover:text-purple-900 ${
                  !permissions.canManageAssignees  || selectedRaidLogs.size !== 1 ? 'text-gray-400': 'text-purple-700 hover:text-purple-900'
                }`}
                disabled={ !permissions.canManageAssignees  || selectedRaidLogs.size !== 1}
                onClick={handleManageAssigneesClick}
              >
                <img
                  src="/icons/noun-team-6990022-644ebd.svg"
                  alt="Manage Assignees"
                  className={`w-8 h-8 mr-3 ${  !permissions.canManageAssignees || selectedRaidLogs.size !== 1 ? 'opacity-50' : ''}`}
                />
                <span className={`flex items-center ${  !permissions.canManageAssignees || selectedRaidLogs.size !== 1  ? 'text-purple-400' : 'text-purple-700'}`}>
                  Manage Assignees

                </span>
              </button>


            </div>

            <div className="flex items-center text-xs text-gray-500 italic mt-4">
              <img
                src="/icons/noun-one-to-many-1406451-676767.svg"
                alt="Info"
                className="w-6 h-6 ml-2"
              />
              -Supports multiple row selection
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Error dialog */}
    {showDialog && (
      <Dialog
        message={dialogMessage}
        onClose={() => setShowDialog(false)}
      />
    )}

    {/* Create RAID Log dialog */}
    <CreateRaidLogDialog
      isOpen={showCreateDialog}
      onClose={() => {
        setShowCreateDialog(false);
        setFormError(''); // Clear any form errors when closing
        setIsEditMode(false);
      }}
      onSubmit={handleSubmitRaidLog}
      isLoading={isCreating}
      newRaidLog={newRaidLog}
      handleInputChange={handleInputChange}
      raidTypeOptions={raidTypeOptions}
      statusOptions={statusOptions}
      priorityOptions={priorityOptions}
      criticalityOptions={criticalityOptions}
      currentUser={userId}
      formError={formError} // Pass the form error
      corporateAccount={corporateAccount}
      setSelectedUserId={setSelectedUserId}
      isEditMode={isEditMode} // Add this line to pass the isEditMode state
      currentProject={currentProject}
      canCreateOrEditRaidLog={permissions.canCreateOrEditRaidLog}
    />






                            <UpdateStatusDialog
                              isOpen={isUpdateStatusDialogOpen}
                              onClose={() => setIsUpdateStatusDialogOpen(false)}
                              onSubmit={handleUpdateStatus}
                              selectedIds={Array.from(selectedRaidLogs)}
                              availableStatuses={allAvailableStatuses}
                            />



                            <UpdatePriorityDialog
                            isOpen={isUpdatePriorityDialogOpen}
                            onClose={() => setIsUpdatePriorityDialogOpen(false)}
                            onSubmit={handleUpdatePriority}
                            selectedIds={Array.from(selectedRaidLogs)}
                            availablePriorities={allAvailablePriorities}
                          />



                            <UpdateCriticalityDialog
                              isOpen={isUpdateCriticalityDialogOpen}
                              onClose={() => setIsUpdateCriticalityDialogOpen(false)}
                              onSubmit={handleUpdateCriticality}
                              selectedIds={Array.from(selectedRaidLogs)}
                              availableCriticalities={allAvailableCriticalities}
                            />


                            <CopyRaidLogDialog
                              isOpen={isCopyDialogOpen}
                              onClose={(success) => {
                                setIsCopyDialogOpen(false);
                                if (success) {
                                  // Refresh the requirements list after successful copy
                                      console.log('Fetch raid log call number 2');
                                    console.log('Mode:', mode);
                                    console.log('AssigneesFilter:', assigneesFilter);
                                    console.log('Corporate Account:', corporateAccount);
                                    console.log('===============================');
                                  fetchRaidLogs();
                                  setselectedRaidLogs(new Set());
                                  //setDialogMessage('Integration requirement copied successfully');
                                  //setShowDialog(true);
                                }
                              }}
                              raidLogToCopy={selectedRaidLogForCopy}
                              corporateAccount={corporateAccount}
                              currentProject={currentProject}
                              login_token={login_token}
                            />






                            <ManageRaidLogCommentsDialog
                              isOpen={isManageRaidLogCommentsDialogOpen}
                              onClose={() => setIsManageRaidLogCommentsDialogOpen(false)}
                              selectedRaidLogs={Array.from(selectedRaidLogs)}
                              corporateAccount={corporateAccount}
                              currentProject={currentProject}
                              login_token={login_token}
                              raidLogType={selectedRaidLogType}
                              raidLogIdWithPrefix={selectedRaidLogIdWithPrefix}
                            />



                            <ManageLinksDialog
                              isOpen={isManageLinksDialogOpen}
                              onClose={() => setIsManageLinksDialogOpen(false)}
                              corporateAccount={corporateAccount}
                              projectId={currentProject}
                              login_token={login_token}
                              sourceRecordType={selectedRaidLogType}
                              selectedSourceIds={selectedRaidLogs}
                              sourceIdWithPrefix={selectedRaidLogIdWithPrefix}
                              canEdit={permissions.canManageLinks}

                            />



                            <ManageRaidLogAssignees
                              isOpen={isManageAssigneesDialogOpen}
                              onClose={() => setIsManageAssigneesDialogOpen(false)}
                              corporateAccount={corporateAccount}
                              projectId={currentProject}
                              login_token={login_token}
                              raidType={selectedRaidLogType}
                              raidId={Array.from(selectedRaidLogs)[0]}
                              raidIdWithPrefix={selectedRaidLogIdWithPrefix}
                              canEdit={permissions.canManageAssignees}
                              userId={userId}

                            />



  </div>
);








};

export default RaidLogManagement;